import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper to load fonts
const loadFont = async (url) => {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network response was not ok');
        const buffer = await response.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    } catch (error) {
        console.error("Error loading font:", url, error);
        return null;
    }
};

// Helper: Draw Horizontal Gradient Rectangle
const drawGradientRect = (doc, x, y, w, h, colorStart, colorEnd) => {
    const steps = Math.ceil(w);
    const stepWidth = w / steps;

    for (let i = 0; i < steps; i++) {
        const t = i / steps;
        const r = Math.round(colorStart[0] * (1 - t) + colorEnd[0] * t);
        const g = Math.round(colorStart[1] * (1 - t) + colorEnd[1] * t);
        const b = Math.round(colorStart[2] * (1 - t) + colorEnd[2] * t);

        doc.setFillColor(r, g, b);
        doc.rect(x + (i * stepWidth), y, stepWidth, h, 'F');
    }
};

export const generateInvoice = async (order, userDetails, action = 'save') => {
    try {
        const doc = new jsPDF();

        // --- Font Loading ---
        const fontRegularUrl = "https://raw.githubusercontent.com/google/fonts/main/ofl/chakrapetch/ChakraPetch-Regular.ttf";
        const fontBoldUrl = "https://raw.githubusercontent.com/google/fonts/main/ofl/chakrapetch/ChakraPetch-Bold.ttf";

        const [fontRegularBase64, fontBoldBase64] = await Promise.all([
            loadFont(fontRegularUrl),
            loadFont(fontBoldUrl)
        ]);

        let customFontLoaded = false;
        if (fontRegularBase64 && fontBoldBase64) {
            doc.addFileToVFS("ChakraPetch-Regular.ttf", fontRegularBase64);
            doc.addFont("ChakraPetch-Regular.ttf", "ChakraPetch", "normal");
            doc.addFileToVFS("ChakraPetch-Bold.ttf", fontBoldBase64);
            doc.addFont("ChakraPetch-Bold.ttf", "ChakraPetch", "bold");
            customFontLoaded = true;
        }

        const setFont = (type = 'normal') => {
            if (customFontLoaded) {
                doc.setFont("ChakraPetch", type);
            } else {
                doc.setFont("helvetica", type === 'bold' ? 'bold' : 'normal');
            }
        };

        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        // --- 1. Background (Dark) ---
        const bgR = 17, bgG = 17, bgB = 17;
        doc.setFillColor(bgR, bgG, bgB);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');

        // --- 2. Watermark ---
        // Center of A4 is 105 x 148.5
        const centerX = pageWidth / 2;
        const centerY = pageHeight / 2;

        doc.saveGraphicsState();
        doc.setFontSize(140);
        doc.setTextColor(30, 30, 30);
        setFont("bold");

        try {
            // Calculated Offset for Optical Centering:
            // Shift X = +30mm, Shift Y = +30mm (Updated based on user preference)
            const offset = 30;
            doc.text("YOYO", centerX + offset, centerY + offset, {
                align: "center",
                angle: 45
            });
        } catch (e) { console.error("Watermark error", e); }
        doc.restoreGraphicsState();

        // --- Colors ---
        const purpleSolid = [168, 85, 247];
        const whiteText = [255, 255, 255];
        const grayText = [156, 163, 175];

        console.log("Generating Invoice:", order._id);

        // --- Header ---
        const topMargin = 25;

        // "YOYO" Title
        doc.setFontSize(40);
        doc.setTextColor(...purpleSolid);
        setFont("bold");
        doc.text("YOYO", 14, topMargin);

        // Subtitle
        doc.setFontSize(10);
        doc.setTextColor(...grayText);
        setFont("normal");
        doc.text("YOUR ULTIMATE GAMING STORE", 14, topMargin + 7);

        // "INVOICE" Title
        doc.setFontSize(26);
        doc.setTextColor(...whiteText);
        setFont("bold");
        doc.text("INVOICE", 196, topMargin, { align: "right" });

        // Gradient Line
        const lineY = topMargin + 10;
        const lineX = 150;
        const lineW = 46;
        drawGradientRect(doc, lineX, lineY, lineW, 1, purpleSolid, [17, 17, 17]);


        // --- Info Section ---
        const startY = 60;

        // Transparent Info Box (Glassmorphism effect) - NO BORDER
        doc.saveGraphicsState();
        try {
            // Fill: White with 5% opacity
            doc.setGState(new doc.GState({ opacity: 0.05 }));
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(14, startY - 8, pageWidth - 28, 40, 2, 2, 'F');
        } catch (e) {
            console.warn("GState transparency failed, parsing fallback", e);
            doc.setFillColor(30, 30, 30);
            doc.roundedRect(14, startY - 8, pageWidth - 28, 40, 2, 2, 'F');
        }
        doc.restoreGraphicsState();

        const leftColX = 20;
        const rightColX = 140;

        // LEFT: Invoice Details
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text("INVOICE DETAILS", leftColX, startY);

        doc.setFontSize(10);
        const rowGap = 7;

        // Number
        doc.setTextColor(...grayText);
        doc.text("Number:", leftColX, startY + rowGap);
        doc.setTextColor(...whiteText);
        setFont("normal");
        doc.text(String(order._id).slice(-8).toUpperCase(), leftColX + 25, startY + rowGap);

        // Date
        doc.setTextColor(...grayText);
        doc.text("Date:", leftColX, startY + rowGap * 2);
        doc.setTextColor(...whiteText);
        doc.text(new Date(order.createdAt).toLocaleDateString(), leftColX + 25, startY + rowGap * 2);

        // Status
        doc.setTextColor(...grayText);
        doc.text("Status:", leftColX, startY + rowGap * 3);

        const statusText = order.status ? order.status.toUpperCase() : "UNKNOWN";
        setFont("bold");
        doc.setFontSize(8);

        const statusW = doc.getTextWidth(statusText) + 8;
        const statusH = 5;
        const statusX = leftColX + 25;
        const statusYn = startY + (rowGap * 3) - 3.5;

        // Badges
        if (order.status === "paid") {
            doc.setDrawColor(52, 211, 153);
            doc.setFillColor(6, 78, 59);
            doc.setTextColor(52, 211, 153);
        } else {
            doc.setDrawColor(250, 204, 21);
            doc.setFillColor(113, 63, 18);
            doc.setTextColor(250, 204, 21);
        }
        doc.setLineWidth(0.1);
        doc.roundedRect(statusX, statusYn, statusW, statusH, 1, 1, 'FD');
        doc.text(statusText, statusX + 4, statusYn + 3.5);

        // RIGHT: Bill To
        setFont("normal");
        doc.setTextColor(...grayText);
        doc.setFontSize(8);
        doc.text("BILL TO", rightColX, startY);

        doc.setFontSize(12);
        doc.setTextColor(...whiteText);
        setFont("bold");
        doc.text(String(userDetails.name || "Customer"), rightColX, startY + rowGap);

        doc.setFontSize(10);
        doc.setTextColor(216, 180, 254);
        setFont("normal");
        doc.text(String(userDetails.email || ""), rightColX, startY + rowGap * 2);


        // --- Table ---
        const tableY = startY + 45;

        // Gradient for Table Header
        const headerStart = [45, 22, 64];
        const headerEnd = [24, 18, 28];
        const tableHeaderHeight = 8;

        drawGradientRect(doc, 14, tableY, pageWidth - 28, tableHeaderHeight, headerStart, headerEnd);

        const tableColumn = ["ITEM", "PLATFORM", "PRICE"];
        const tableRows = [];

        if (order.items && Array.isArray(order.items)) {
            order.items.forEach((item) => {
                tableRows.push([
                    item.game?.title || "Unknown Game",
                    (item.platform || "-").toUpperCase(),
                    `$${Number(item.price).toFixed(2)}`,
                ]);
            });
        }

        autoTable(doc, {
            startY: tableY,
            head: [tableColumn],
            body: tableRows,
            theme: 'grid',
            headStyles: {
                fillColor: false,
                textColor: [233, 213, 255],
                fontStyle: 'bold',
                lineWidth: 0,
                halign: 'left',
                minCellHeight: tableHeaderHeight,
                valign: 'middle'
            },
            bodyStyles: {
                fillColor: false,
                textColor: [255, 255, 255],
                lineWidth: 0,
                minCellHeight: 10,
                valign: 'middle'
            },
            alternateRowStyles: {
                fillColor: false
            },
            columnStyles: {
                0: { cellWidth: 'auto' },
                1: { cellWidth: 40, textColor: [156, 163, 175] },
                2: { cellWidth: 30, halign: 'right', fontStyle: 'bold', textColor: [52, 211, 153] }
            },
            styles: {
                font: customFontLoaded ? 'ChakraPetch' : 'helvetica',
                fontSize: 10,
                cellPadding: 3,
                lineColor: [40, 40, 40],
                lineWidth: { bottom: 0.1 }
            },
        });

        // --- Totals ---
        // ADDED SPACE: increased gap from 15 to 30
        const finalY = (doc.lastAutoTable?.finalY || 150) + 30;

        // Container
        const boxW = 80;
        const boxH = 50;
        const boxX = pageWidth - boxW - 14;

        // Transparent Totals Box - NO BORDER
        doc.saveGraphicsState();
        try {
            doc.setGState(new doc.GState({ opacity: 0.05 }));
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(boxX, finalY, boxW, boxH, 2, 2, 'F');
        } catch (e) {
            doc.setFillColor(22, 22, 22);
            doc.roundedRect(boxX, finalY, boxW, boxH, 2, 2, 'F');
        }
        doc.restoreGraphicsState();

        let cy = finalY + 10;
        const lx = boxX + 8;
        const vx = boxX + boxW - 8;

        // Calc
        let subtotal = 0;
        if (order.items) subtotal = order.items.reduce((s, i) => s + (Number(i.price) || 0), 0);
        const tax = subtotal * 0.18;
        const discount = subtotal * 0.05;

        setFont("normal");
        doc.setFontSize(10);

        // Subtotal
        doc.setTextColor(...grayText);
        doc.text("Subtotal", lx, cy);
        doc.setTextColor(...whiteText);
        doc.text(`$${subtotal.toFixed(2)}`, vx, cy, { align: "right" });
        cy += 7;

        // Tax
        doc.setTextColor(...grayText);
        doc.text("Tax (18%)", lx, cy);
        doc.setTextColor(...whiteText);
        doc.text(`+$${tax.toFixed(2)}`, vx, cy, { align: "right" });
        cy += 7;

        // Discount
        doc.setTextColor(...grayText);
        doc.text("Discount (5%)", lx, cy);
        doc.setTextColor(52, 211, 153);
        doc.text(`-$${discount.toFixed(2)}`, vx, cy, { align: "right" });

        cy += 12;

        // Divider
        doc.setDrawColor(60, 60, 60);
        doc.line(lx, cy - 5, vx, cy - 5);

        // Total
        setFont("bold");
        doc.setFontSize(14);
        doc.setTextColor(192, 132, 252);
        doc.text("Total", lx, cy + 2);
        doc.setTextColor(...whiteText);
        doc.text(`$${order.amount}`, vx, cy + 2, { align: "right" });

        // --- Footer ---
        const footerY = pageHeight - 15;
        doc.setDrawColor(40, 40, 40);
        doc.line(14, footerY - 10, pageWidth - 14, footerY - 10);

        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        setFont("normal");
        doc.text("Thank you for shopping with YOYO!", pageWidth / 2, footerY, { align: "center" });

        doc.setFontSize(8);
        doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, footerY + 5, { align: "center" });

        // --- Save ---
        if (action === 'open') {
            window.open(doc.output('bloburl'), '_blank');
        } else if (action === 'blob') {
            return doc.output('bloburl');
        } else {
            doc.save(`Invoice_${order._id}.pdf`);
        }

    } catch (err) {
        console.error("GENERATE INVOICE ERROR", err);
        alert("Failed to generate PDF");
    }
};
